/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CLIENT_DIAGNOSES,
  CreateClientSchema,
  InviteParentSchema,
} from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, useState, type JSX } from 'react';
import { type FieldErrors, type UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type Client, type ParentInvite, type Therapist } from '../../lib/api/resources.js';
import {
  useCreateClient,
  useInviteParent,
  useTherapists,
  useUpdateClient,
} from '../../lib/queries/clinical.js';

import { InviteResult } from './invite-parent-form.js';

/**
 * Client form schema = the child fields (incl. parent/guardian name + the
 * parent's email), plus an "also create a parent login" toggle. When the toggle
 * is on, the guardian name + email must be valid (the same shared
 * `InviteParentSchema`) so we can provision the Parent-portal login.
 */
const ClientWithInviteSchema = CreateClientSchema.extend({
  inviteParentNow: z.boolean().optional(),
}).superRefine((value, ctx) => {
  if (value.inviteParentNow !== true) {
    return;
  }
  const parsed = InviteParentSchema.safeParse({
    fullName: value.guardianName ?? '',
    email: value.email ?? '',
  });
  if (parsed.success) {
    return;
  }
  for (const issue of parsed.error.issues) {
    const path = issue.path[0] === 'email' ? ['email'] : ['guardianName'];
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: issue.message });
  }
});
type ClientWithInviteInput = z.infer<typeof ClientWithInviteSchema>;

interface ClientFormProps {
  readonly onDone: () => void;
  readonly client?: Client | undefined;
}

function emptyToUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function numberOrUndefined(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function defaultsFrom(client: Client | undefined): Partial<ClientWithInviteInput> {
  if (client === undefined) {
    // New client: default the parent-login toggle on (the admin can untick it).
    return { inviteParentNow: true };
  }
  return {
    fullName: client.fullName,
    age: client.age ?? undefined,
    primaryDiagnosis: client.primaryDiagnosis ?? undefined,
    guardianName: client.guardianName ?? undefined,
    email: client.email ?? undefined,
    phone: client.phone ?? undefined,
    sessionsPerWeek: client.sessionsPerWeek ?? undefined,
  };
}

interface FieldsProps {
  readonly register: UseFormRegister<ClientWithInviteInput>;
  readonly errors: FieldErrors<ClientWithInviteInput>;
  readonly therapists: readonly Therapist[];
  readonly isEdit: boolean;
}

function DiagnosisField({
  register,
  errors,
}: Omit<FieldsProps, 'therapists' | 'isEdit'>): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <label
        htmlFor="client-diagnosis"
        className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {t('form.client.primaryDiagnosis')}
      </label>
      <select
        id="client-diagnosis"
        className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        aria-invalid={errors.primaryDiagnosis !== undefined}
        {...register('primaryDiagnosis', { setValueAs: emptyToUndefined })}
      >
        <option value="">{t('form.client.selectDiagnosis')}</option>
        {CLIENT_DIAGNOSES.map((dx) => (
          <option key={dx} value={dx}>
            {dx}
          </option>
        ))}
      </select>
    </div>
  );
}

/** The Figma client fields (Full Name, Age, Diagnosis, Parent Email, Phone, Guardian, Therapist). */
function ClientFields({ register, errors, therapists, isEdit }: FieldsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <FormField
        id="client-fullName"
        type="text"
        label={t('form.client.fullName')}
        autoComplete="off"
        error={errors.fullName?.message}
        registration={register('fullName')}
      />
      <FormField
        id="client-age"
        type="number"
        label={t('form.client.age')}
        autoComplete="off"
        error={errors.age?.message}
        registration={register('age', { setValueAs: numberOrUndefined })}
      />
      <DiagnosisField register={register} errors={errors} />
      <FormField
        id="client-email"
        type="email"
        label={t('form.client.parentEmail')}
        autoComplete="email"
        error={errors.email?.message}
        registration={register('email', { setValueAs: emptyToUndefined })}
      />
      <FormField
        id="client-phone"
        type="tel"
        label={t('form.client.phone')}
        autoComplete="off"
        error={errors.phone?.message}
        registration={register('phone', { setValueAs: emptyToUndefined })}
      />
      <FormField
        id="client-guardianName"
        type="text"
        label={t('form.client.guardianName')}
        autoComplete="off"
        error={errors.guardianName?.message}
        registration={register('guardianName', { setValueAs: emptyToUndefined })}
      />
      <FormField
        id="client-sessionsPerWeek"
        type="number"
        label={t('form.client.sessionsPerWeek')}
        autoComplete="off"
        error={errors.sessionsPerWeek?.message}
        registration={register('sessionsPerWeek', { setValueAs: numberOrUndefined })}
      />
      {!isEdit && (
        <div>
          <label
            htmlFor="client-therapist"
            className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {t('form.client.assignTherapist')}
          </label>
          <select
            id="client-therapist"
            className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            defaultValue=""
            {...register('assignedTherapistId', { setValueAs: emptyToUndefined })}
          >
            <option value="">{t('form.client.noTherapist')}</option>
            {therapists.map((th) => (
              <option key={th.id} value={th.id}>
                {th.fullName}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}

/** "Also create a parent login" toggle (create flow). On by default; uses the Parent Email above. */
function ParentLoginToggle({
  register,
}: {
  readonly register: UseFormRegister<ClientWithInviteInput>;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        <input type="checkbox" className="focus-ring h-4 w-4" {...register('inviteParentNow')} />
        {t('form.client.inviteParentNow')}
      </label>
      <p className="mt-1 text-xs text-slate-400">{t('form.client.inviteParentHint')}</p>
    </div>
  );
}

/** "Add/Edit Client" form. On create, can also invite + email a parent inline. */
export function ClientForm({ onDone, client }: ClientFormProps): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateClient();
  const update = useUpdateClient();
  const invite = useInviteParent();
  const therapists = useTherapists();
  const isEdit = client !== undefined;
  const [result, setResult] = useState<ParentInvite | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ClientWithInviteInput>({
    resolver: zodResolver(ClientWithInviteSchema),
    defaultValues: defaultsFrom(client),
  });

  const submit = useCallback(
    async (values: ClientWithInviteInput): Promise<void> => {
      const { inviteParentNow, ...clientInput } = values;
      if (client !== undefined) {
        await update.mutateAsync({ id: client.id, input: clientInput });
        onDone();
        return;
      }
      const created = await create.mutateAsync(clientInput);
      const email = emptyToUndefined(clientInput.email);
      if (
        inviteParentNow === true &&
        clientInput.guardianName !== undefined &&
        email !== undefined
      ) {
        const data = await invite.mutateAsync({
          id: created.id,
          input: { fullName: clientInput.guardianName, email },
        });
        setResult(data);
        return;
      }
      onDone();
    },
    [client, create, update, invite, onDone],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  if (result !== null) {
    return <InviteResult invite={result} onDone={onDone} />;
  }

  const saveError = create.isError || update.isError;
  const pending = isSubmitting || [create, update, invite].some((m) => m.isPending);

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <ClientFields
        register={register}
        errors={errors}
        therapists={therapists.data ?? []}
        isEdit={isEdit}
      />
      {!isEdit && <ParentLoginToggle register={register} />}

      {saveError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      {invite.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('admin.invite.error')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending}>
          {isEdit ? t('common.save') : t('form.client.submit')}
        </Button>
      </div>
    </form>
  );
}
